const FORBIDDEN_KEYS = new Set([
  'children',
  'className',
  'style',
  'height',
  'width',
  'render',
  'component',
  'jsx',
  'map',
  'geojson',
  'media',
  'form',
]);

export function failFormFlow(path: string, message: string): never {
  throw new Error(`Invalid form flow descriptor: ${path} ${message}.`);
}

export function formFlowObject(
  value: unknown,
  path: string,
): Record<string, unknown> {
  if (
    !value
    || typeof value !== 'object'
    || Array.isArray(value)
    || Object.getPrototypeOf(value) !== Object.prototype
  ) {
    return failFormFlow(path, 'must be a plain object');
  }
  return value as Record<string, unknown>;
}

export function exactFormFlowKeys(
  value: Record<string, unknown>,
  allowed: ReadonlySet<string>,
  path: string,
): void {
  for (const key of Object.keys(value)) {
    if (FORBIDDEN_KEYS.has(key)) {
      failFormFlow(`${path}.${key}`, 'is renderer-owned and forbidden');
    }
    if (!allowed.has(key)) {
      failFormFlow(`${path}.${key}`, 'is not part of the sealed contract');
    }
  }
}

export function formFlowText(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    return failFormFlow(path, 'must be non-empty plain text');
  }
  return value;
}

export function optionalFormFlowText(value: unknown, path: string): void {
  if (value !== undefined) formFlowText(value, path);
}

export function formFlowArray(
  value: unknown,
  path: string,
): readonly unknown[] {
  if (!Array.isArray(value)) return failFormFlow(path, 'must be an array');
  return value;
}

export function formFlowOneOf(
  value: unknown,
  values: readonly string[],
  path: string,
): void {
  if (!values.includes(formFlowText(value, path))) {
    failFormFlow(path, `must be one of ${values.join(', ')}`);
  }
}

export function formFlowBoolean(value: unknown, path: string): void {
  if (typeof value !== 'boolean') failFormFlow(path, 'must be boolean');
}

export function formFlowScalar(value: unknown, path: string): void {
  if (
    value !== null
    && !['string', 'number', 'boolean'].includes(typeof value)
  ) {
    failFormFlow(path, 'must be a scalar value or null');
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    failFormFlow(path, 'must be finite');
  }
}

export function formFlowSelectValue(
  value: unknown,
  path: string,
  nullable: boolean,
): void {
  if (nullable && value === null) return;
  if (typeof value !== 'string' && typeof value !== 'number') {
    failFormFlow(path, 'must be text or a finite number');
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    failFormFlow(path, 'must be finite');
  }
}

export function formFlowChoiceValue(value: unknown, path: string): void {
  if (typeof value !== 'string') failFormFlow(path, 'must be text');
}

export function uniqueFormFlowIds(
  items: readonly unknown[],
  path: string,
): Set<string> {
  const ids = new Set<string>();
  items.forEach((item, index) => {
    const id = formFlowText(
      formFlowObject(item, `${path}[${index}]`).id,
      `${path}[${index}].id`,
    );
    if (ids.has(id)) failFormFlow(`${path}[${index}].id`, 'must be unique');
    ids.add(id);
  });
  return ids;
}

export function nonEmptyFormFlowTextArray(
  value: unknown,
  path: string,
): string[] {
  const items = formFlowArray(value, path);
  if (!items.length) failFormFlow(path, 'must contain at least one item');
  return items.map((item, index) => (
    formFlowText(item, `${path}[${index}]`)
  ));
}
